// NPM
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Loader from '../../components/common/Loader';
import Paginator from '../../components/common/Paginator';
import Layout from '../../components/layouts/Layout';
import formattedMessages from '../../messages/account/AdminFormattedMessages';
import useAdministratorRequest from '../../requests/AdministratorRequest';
import { AccountRoutes } from '../../routes/AccountRoutes';
import Config from '../../Config';

// I18N

// CONFIG

const Admins = ({ accessLevel, accessType, errorHandler, customer }) => {
  const theme = useTheme();
  const { getAdministrators } = useAdministratorRequest();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(Config.defaultPL);
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [letters, setLetters] = useState([]);
  const [filterLetter, setFilterLetter] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();

  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [contactTypeFilter, setContactTypeFilter] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useState({
    name: '',
    email: '',
    contactType: '',
  });

  const loadAdministrators = async () => {
    setLoading(true);
    try {
      const response = await getAdministrators(
        filterLetter,
        pageNumber,
        pageLength,
        errorHandler
      );

      if (response) {
        setAdmins(response.items || []);
        setLetters(response.letters || []);
        setTotalCount(response.rowCount || 0);
      }
    } catch (error) {
      console.error('Error loading administrators:', error);
      errorHandler?.(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdministrators();
  }, [filterLetter, pageNumber, pageLength]);

  const handlePageChange = (newPageIndex) => {
    setPageNumber(newPageIndex);
  };

  const handlePageLengthChange = (event) => {
    const newLength = parseInt(event.target.value, 10);
    setPageLength(newLength);
    setPageNumber(1);
  };

  const handleLetterFilter = (letter) => {
    setFilterLetter(letter === filterLetter ? '' : letter);
    setPageNumber(1);
  };

  const handleTableRowClick = (admin) => {
    if (!loading) {
      navigate(`/account/administrators/${admin.customer_contact_id}`);
    }
  };

  const handleSearch = () => {
    setIsSearching(true);
    setSearchParams({
      name: nameFilter.trim(),
      email: emailFilter.trim(),
      contactType: contactTypeFilter,
    });
    setIsSearching(false);
  };

  const handleClearSearch = () => {
    setNameFilter('');
    setEmailFilter('');
    setContactTypeFilter('');
    setSearchParams({
      name: '',
      email: '',
      contactType: '',
    });
  };

  if (accessLevel <= '0' || accessType === '0') {
    return null;
  }

  const renderAdminTable = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={5} sx={{ paddingY: 4, textAlign: 'center' }}>
            <Loader />
          </TableCell>
        </TableRow>
      );
    }

    if (!admins || admins.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} sx={{ paddingY: 2 }}>
            <Typography>No administrators found</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return admins.map((admin) => {
      const contactType = [];

      if (admin.sales_contact !== 'N') {
        contactType.push(
          `${admin.sales_contact === 'P' ? 'Primary' : 'Secondary'} Sales`
        );
      }
      if (admin.billing_contact !== 'N') {
        contactType.push(
          `${admin.billing_contact === 'P' ? 'Primary' : 'Secondary'} Billing`
        );
      }
      if (admin.technical_contact !== 'N') {
        contactType.push(
          `${
            admin.technical_contact === 'P' ? 'Primary' : 'Secondary'
          } Technical`
        );
      }

      return (
        <TableRow
          key={admin.customer_contact_id}
          onClick={() => handleTableRowClick(admin)}
          hover
        >
          <TableCell>{admin.contact_name}</TableCell>
          <TableCell>{admin.contact_email}</TableCell>
          <TableCell>{admin.contact_dept}</TableCell>
          <TableCell>{admin.access_level_meaning}</TableCell>
          <TableCell>{contactType.join(', ')}</TableCell>
          <TableCell></TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Layout
      routes={AccountRoutes}
      title="Administrators"
      accessLevel={accessLevel}
      accessType={accessType}
      isHidden={!customer.hasCampaign}
    >
      <Button
        variant="contained"
        color="secondary"
        onClick={() => navigate('/account/administrators/new')}
      >
        {formattedMessages.addNewAdmin}
      </Button>

      <Stack direction="row" sx={{ marginTop: 2, marginBottom: 0.25 }}>
        {(letters || []).map((letter) => (
          <Button
            key={letter}
            variant={'text'}
            onClick={() => handleLetterFilter(letter)}
            size="small"
            sx={{
              minWidth: 'auto',
              padding: 0.25,
              color:
                filterLetter === letter ? 'primary.main' : 'secondary.light',
            }}
          >
            {letter}
          </Button>
        ))}
      </Stack>

      <TableContainer
        sx={{ marginBottom: 4, overflowX: 'auto', whiteSpace: 'nowrap' }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{formattedMessages.name}</TableCell>
              <TableCell>{formattedMessages.email}</TableCell>
              <TableCell>{formattedMessages.department}</TableCell>
              <TableCell>{formattedMessages.accessLevel}</TableCell>
              <TableCell>{formattedMessages.contactType}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderAdminTable()}</TableBody>
        </Table>
      </TableContainer>

      <Paginator
        pageNumber={pageNumber}
        setPageNumber={handlePageChange}
        itemCount={totalCount}
        pageLength={pageLength}
        changeEvent={handlePageChange}
        editFunction={handlePageLengthChange}
        showTotal="Administrators Found"
        disabled={loading}
      />
    </Layout>
  );
};

export default Admins;
